import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card } from './ui/card';
import { Calendar, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryTattoos, setGalleryTattoos] = useState([]);
  const [galleryEvents, setGalleryEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        // Fetch tattoo gallery
        const tattoosResponse = await axios.get(`${BACKEND_URL}/api/admin/gallery`);
        setGalleryTattoos(tattoosResponse.data);

        // Fetch events
        const eventsResponse = await axios.get(`${BACKEND_URL}/api/admin/events`);
        setGalleryEvents(eventsResponse.data);
      } catch (error) {
        console.error('Failed to load gallery:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryData();
  }, []);

  return (
    <section id="gallery" className="py-20 px-6 bg-zinc-950">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-4 uppercase tracking-tight">
            Our <span className="text-red-600">Work</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Explore our portfolio of stunning tattoos and memorable events
          </p>
        </div>

        <Tabs defaultValue="tattoos" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 bg-zinc-900 border border-zinc-800">
            <TabsTrigger 
              value="tattoos" 
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-bold uppercase tracking-wider"
            >
              Tattoo Work
            </TabsTrigger>
            <TabsTrigger 
              value="events" 
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-bold uppercase tracking-wider"
            >
              Events
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tattoos" className="mt-0">
            {loading ? (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg">Loading gallery...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {galleryTattoos.map((item) => (
                  <Card 
                    key={item.id}
                    className="gallery-card group relative overflow-hidden bg-zinc-900 border-zinc-800 cursor-pointer transition-all duration-300 hover:border-red-600 hover:shadow-xl hover:shadow-red-600/20"
                    onClick={() => setSelectedImage(item)}
                  >
                    <div className="aspect-square overflow-hidden">
                      <img 
                        src={`${BACKEND_URL}${item.image_url}`}
                        alt={item.style}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                      <p className="text-red-500 font-bold uppercase text-sm tracking-wider">{item.style}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="events" className="mt-0">
            {loading ? (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg">Loading events...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {galleryEvents.map((event) => (
                  <Card 
                    key={event.id}
                    className="gallery-card group relative overflow-hidden bg-zinc-900 border-zinc-800 transition-all duration-300 hover:border-red-600 hover:shadow-xl hover:shadow-red-600/20"
                  >
                    <div className="aspect-video overflow-hidden bg-zinc-800">
                      <video 
                        src={`${BACKEND_URL}${event.video_url}`}
                        controls
                        className="w-full h-full object-contain"
                        preload="metadata"
                      />
                    </div>
                    <div className="p-4 bg-zinc-900">
                      <h3 className="text-white text-lg font-bold mb-2">{event.title}</h3>
                      <div className="flex items-center gap-2 text-red-500">
                        <Calendar className="w-4 h-4" />
                        <span className="font-semibold text-sm">{event.date}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Gallery;
